import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { AUTO_SAVE_INTERVAL_MS } from "@/lib/constants";
import { formatFieldValue, formatFormData, formatDateTimeIST, formatDateIST, formatTimeIST } from "@/lib/formatUtils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, LogOut, FileEdit, Plus, Gauge, Fuel } from "lucide-react";
import DraftSection from "@/components/shared/DraftSection";
import ExportButtons from "@/components/shared/ExportButtons";
import EntryDetailDialog from "@/components/shared/EntryDetailDialog";
import logger from "@/lib/logger";
import { VehicleEntryForm } from "./vehicle/EntryForm";
import MeterLogForm from "./vehicle/MeterLogForm";
import FuelLogForm from "./vehicle/FuelLogForm";
import OtherValueDialog from "@/components/shared/OtherValueDialog";

const TAB_CONFIG = {
  gate: { label: "Gate In/Out", api: "/mod5/vehicle", collection: "vehicle_movements" },
  meter: { label: "Meter Log", api: "/mod5/meter-log", collection: "meter_logs" },
  fuel: { label: "Fuel Log", api: "/mod5/fuel-log", collection: "fuel_logs" },
};

// Stable empty array reference — prevents `columns={[]}` from creating a new prop each render.
const EMPTY_COLUMNS = [];

export default function VehicleMovement() {
  const [tab, setTab] = useState("gate");
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [detailEntry, setDetailEntry] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [parties, setParties] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [otherField, setOtherField] = useState(null);
  const [otherValue, setOtherValue] = useState("");
  const [closeTripEntry, setCloseTripEntry] = useState(null);
  const [closeTripForm, setCloseTripForm] = useState({});
  const autoSaveRef = useRef(null);
  const { location } = useAuth();

  const cfg = TAB_CONFIG[tab];

  const fetchEntries = useCallback(async () => {
    try {
      const params = { location, search: search || undefined };
      if ((tab === "meter" || tab === "fuel") && vehicleFilter) params.vehicle_no = vehicleFilter;
      const { data } = await api.get(cfg.api, { params });
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (err) { logger.error("Failed to fetch entries:", err); }
  }, [location, search, tab, cfg.api, vehicleFilter]);

  const fetchDrafts = useCallback(async () => {
    if (tab !== "gate") { setDrafts([]); return; }
    try {
      const { data } = await api.get("/drafts/vehicle", { params: { location } });
      setDrafts(data.drafts || []);
    } catch (err) { logger.error("Failed to fetch drafts:", err); }
  }, [location, tab]);

  useEffect(() => { fetchEntries(); fetchDrafts(); }, [fetchEntries, fetchDrafts]);

  const loadMasters = useCallback(async () => {
    try {
      const [v, p, veh] = await Promise.all([
        api.get("/masters/vehicle_types"),
        api.get("/masters/parties"),
        api.get("/masters/vehicles"),
      ]);
      setVehicleTypes(v.data.items);
      setParties(p.data.items);
      setVehicles(veh.data.items);
    } catch (err) { logger.error("Failed to load masters:", err); }
  }, []);

  useEffect(() => { loadMasters(); }, [loadMasters]);

  // Auto-save for gate tab only
  const formRef = useRef(form);
  formRef.current = form;
  const draftIdRef = useRef(currentDraftId);
  draftIdRef.current = currentDraftId;

  // Auto-save draft every AUTO_SAVE_INTERVAL_MS. `api` and refs are stable module/ref imports
  // and AUTO_SAVE_INTERVAL_MS is a constant, so they are intentionally excluded from deps.
  useEffect(() => {
    if (!showForm || tab !== "gate") return;
    autoSaveRef.current = setInterval(async () => {
      const f = formRef.current;
      if (!f || Object.keys(f).length === 0) return;
      try {
        const payload = { ...f, location, _draft_id: draftIdRef.current || undefined };
        const { data } = await api.post("/drafts/vehicle", payload);
        if (!draftIdRef.current && data.id) setCurrentDraftId(data.id);
        setLastAutoSave(new Date());
      } catch (err) { logger.error("Auto-save failed:", err); }
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(autoSaveRef.current);
  }, [showForm, location, tab]);

  const handleSaveDraft = async () => {
    try {
      const payload = { ...form, location, _draft_id: currentDraftId || undefined };
      const { data } = await api.post("/drafts/vehicle", payload);
      if (!currentDraftId && data.id) setCurrentDraftId(data.id);
      toast.success("Draft saved");
      setLastAutoSave(new Date());
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleSubmit = async () => {
    if (tab === "gate") {
      if (!form.vehicle_no?.trim()) { toast.error("Vehicle No. is required"); return; }
      if (!form.driver_name?.trim()) { toast.error("Driver Name is required"); return; }
      const partiesList = form.parties_list?.length > 0 ? form.parties_list : (form.party_company ? [form.party_company] : []);
      if (partiesList.length === 0) { toast.error("At least one Party / Company is required"); return; }
      setSaving(true);
      try {
        await api.post(cfg.api, { ...formatFormData(form), parties_list: partiesList, party_company: partiesList.join(", "), location });
        toast.success("Vehicle entry created");
        if (currentDraftId) await api.post(`/drafts/vehicle/${currentDraftId}/submit`).catch(() => {});
        closeForm(); fetchEntries(); fetchDrafts();
      } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
      setSaving(false);
    } else if (tab === "meter") {
      if (!form.vehicle_no?.trim()) { toast.error("Vehicle is required"); return; }
      if (!form.odometer_out) { toast.error("Odometer Out reading is required"); return; }
      setSaving(true);
      try {
        await api.post(cfg.api, { ...form, location });
        toast.success("Meter log created");
        closeForm(); fetchEntries();
      } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
      setSaving(false);
    } else if (tab === "fuel") {
      if (!form.vehicle_no?.trim()) { toast.error("Vehicle is required"); return; }
      if (!form.quantity_litres) { toast.error("Fuel quantity is required"); return; }
      if (!form.amount) { toast.error("Amount is required"); return; }
      if (!form.odometer_reading) { toast.error("Odometer reading is required"); return; }
      setSaving(true);
      try {
        const { data } = await api.post(cfg.api, { ...form, location });
        const msg = data.mileage_kmpl > 0 ? `Fuel log created — Mileage: ${data.mileage_kmpl} km/l` : "Fuel log created";
        toast.success(msg);
        closeForm(); fetchEntries();
      } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
      setSaving(false);
    }
  };

  const handleCheckout = async (id) => {
    try {
      await api.put(`/mod5/vehicle/${id}/checkout`, {});
      toast.success("Vehicle exited");
      fetchEntries();
    } catch { toast.error("Failed"); }
  };

  const handleOpenDraft = async (draft) => {
    try {
      const { data } = await api.get(`/drafts/vehicle/${draft.id}`);
      setForm(data.data || {});
      setCurrentDraftId(draft.id);
      setShowForm(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot open draft"); }
  };

  const handleDeleteDraft = async (draftId) => {
    try { await api.delete(`/drafts/vehicle/${draftId}`); toast.success("Draft deleted"); fetchDrafts(); } catch { toast.error("Failed"); }
  };

  const handleSelectOrOther = (fieldKey, val, masterType) => {
    if (val === "__other__") { setOtherField({ masterType, fieldKey }); return; }
    setField(fieldKey, val);
  };

  const fetchMasters = () => {
    Promise.all([api.get("/masters/vehicle_types"), api.get("/masters/parties"), api.get("/masters/vehicles")]).then(([v, p, veh]) => {
      setVehicleTypes(v.data.items);
      setParties(p.data.items);
      setVehicles(veh.data.items);
    }).catch((err) => { logger.error("Failed to refresh masters:", err); });
  };

  const handleOtherSubmit = async () => {
    if (!otherValue.trim() || !otherField) return;
    const val = formatFieldValue(otherField.fieldKey, otherValue.trim());
    if (otherField.fieldKey.startsWith("_party_")) {
      const idx = parseInt(otherField.fieldKey.split("_")[2], 10);
      const list = [...(form.parties_list || [""])];
      list[idx] = val;
      setField("parties_list", list);
    } else {
      setField(otherField.fieldKey, val);
    }
    try { await api.post(`/masters/${otherField.masterType}`, { name: val }); } catch (err) { logger.error("Failed to save master:", err); }
    fetchMasters();
    toast.success(`"${val}" added`);
    setOtherField(null); setOtherValue("");
  };

  const closeForm = () => {
    setShowForm(false);
    if (currentDraftId) api.post(`/drafts/vehicle/${currentDraftId}/release-lock`).catch((err) => { logger.error("Lock release failed:", err); });
    setCurrentDraftId(null);
    setLastAutoSave(null);
    setForm({});
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: formatFieldValue(k, v) }));

  const newBtnLabels = { gate: "New Vehicle", meter: "New Meter Log", fuel: "New Fuel Log" };
  const formTitles = { gate: "New Vehicle Entry", meter: "New Meter Log Entry", fuel: "New Fuel Log Entry" };

  // Unique vehicles in entries for filter dropdown
  const entryVehicles = [...new Set(entries.map(e => e.vehicle_no).filter(Boolean))].sort();

  return (
    <div className="space-y-5" data-testid="vehicle-movement-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Chivo" }}>Vehicle Movement</h1>
          <p className="text-xs sm:text-sm text-gray-500">MOD-5 · Gate Tracking, Meter Log & Fuel Log</p>
        </div>
        <Button onClick={() => { setForm(tab === "fuel" ? { fuel_type: "Diesel" } : {}); setCurrentDraftId(null); setShowForm(true); }} className="bg-[#1B3C87] hover:bg-[#2650A8] h-10 sm:h-11 text-xs sm:text-sm w-full sm:w-auto" data-testid="new-entry-btn">
          <Plus size={18} className="mr-1" /> {newBtnLabels[tab]}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={v => { setTab(v); setSearch(""); setVehicleFilter(""); }}>
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="gate" data-testid="tab-gate">Gate In/Out</TabsTrigger>
          <TabsTrigger value="meter" data-testid="tab-meter"><Gauge size={14} className="mr-1" />Meter Log</TabsTrigger>
          <TabsTrigger value="fuel" data-testid="tab-fuel"><Fuel size={14} className="mr-1" />Fuel Log</TabsTrigger>
        </TabsList>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mt-3">
          <ExportButtons reportType={cfg.collection} title={cfg.label} entries={entries} columns={EMPTY_COLUMNS} location={location} />
          <div className="flex items-center gap-2">
            {(tab === "meter" || tab === "fuel") && (
              <Select value={vehicleFilter} onValueChange={v => setVehicleFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9 w-44" data-testid="vehicle-filter">
                  <SelectValue placeholder="All Vehicles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vehicles</SelectItem>
                  {entryVehicles.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 w-full sm:w-52" data-testid="search-input" />
            </div>
          </div>
        </div>

        {/* Gate In/Out Tab */}
        <TabsContent value="gate">
          {tab === "gate" && <DraftSection drafts={drafts} onOpen={handleOpenDraft} onDelete={handleDeleteDraft} draftType="vehicle" />}
          <Card className="mt-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                  {["Ref No.", "Vehicle No.", "Type", "Driver", "Party/Company", "In Time", "Out Time", "Status", "Actions"].map(h => (
                    <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400">No entries</TableCell></TableRow>}
                {entries.map(e => (
                  <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                    <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                    <TableCell className="font-medium">{e.vehicle_no || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.vehicle_type || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.driver_name || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.party_company || "\u2014"}</TableCell>
                    <TableCell className="text-xs">{e.in_time ? formatDateTimeIST(e.in_time) : "\u2014"}</TableCell>
                    <TableCell className="text-xs">{e.out_time ? formatDateTimeIST(e.out_time) : "\u2014"}</TableCell>
                    <TableCell>
                      <span className={`status-${e.status} px-2 py-0.5 rounded text-xs font-semibold`}>{e.status?.replace("_", " ")}</span>
                    </TableCell>
                    <TableCell>
                      {e.status === "on_premises" && (
                        <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={(ev) => { ev.stopPropagation(); handleCheckout(e.id); }} data-testid={`checkout-${e.id}`}>
                          <LogOut size={12} className="mr-1" /> Exit
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
          </Card>
        </TabsContent>

        {/* Meter Log Tab */}
        <TabsContent value="meter">
          <Card className="mt-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                  {["Ref No.", "Vehicle", "Owner", "Driver", "Odo Out", "Odo In", "Km Run", "Status", "Date", "Action"].map(h => (
                    <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-8 text-gray-400">No entries</TableCell></TableRow>}
                {entries.map(e => {
                  const tripStatus = e.status || (e.odometer_in > 0 ? "closed" : "open");
                  return (
                  <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                    <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                    <TableCell className="font-medium">{e.vehicle_no || "\u2014"}{e.vehicle_name ? <span className="text-gray-400 text-xs ml-1">({e.vehicle_name})</span> : ""}</TableCell>
                    <TableCell><span className={`px-2 py-0.5 rounded text-xs font-semibold ${e.owner_type === "Employee" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{e.owner_type || "Company"}</span></TableCell>
                    <TableCell className="text-sm">{e.driver_name || e.employee_name || "\u2014"}</TableCell>
                    <TableCell className="text-sm font-mono">{e.odometer_out || 0}</TableCell>
                    <TableCell className="text-sm font-mono">{e.odometer_in > 0 ? e.odometer_in : <span className="text-gray-300">—</span>}</TableCell>
                    <TableCell className="text-sm font-bold text-emerald-700">{e.km_run || 0} km</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${tripStatus === "open" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`meter-status-${e.id}`}>
                        {tripStatus.toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{e.created_at ? formatDateIST(e.created_at) : "\u2014"}</TableCell>
                    <TableCell onClick={(ev) => ev.stopPropagation()}>
                      {tripStatus === "open" && (
                        <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => { setCloseTripEntry(e); setCloseTripForm({ odometer_in: "" }); }} data-testid={`close-trip-${e.id}`}>
                          Close Trip
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
          </Card>
        </TabsContent>

        {/* Fuel Log Tab */}
        <TabsContent value="fuel">
          <Card className="mt-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                  {["Ref No.", "Vehicle", "Owner", "Fuel", "Litres", "Amount", "Odometer", "Mileage", "Date"].map(h => (
                    <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400">No entries</TableCell></TableRow>}
                {entries.map(e => (
                  <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                    <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                    <TableCell className="font-medium">{e.vehicle_no || "\u2014"}{e.vehicle_name ? <span className="text-gray-400 text-xs ml-1">({e.vehicle_name})</span> : ""}</TableCell>
                    <TableCell><span className={`px-2 py-0.5 rounded text-xs font-semibold ${e.owner_type === "Employee" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{e.owner_type || "Company"}</span></TableCell>
                    <TableCell className="text-sm">{e.fuel_type || "\u2014"}</TableCell>
                    <TableCell className="text-sm font-mono">{e.quantity_litres || 0} L</TableCell>
                    <TableCell className="text-sm">Rs. {e.amount || 0}</TableCell>
                    <TableCell className="text-sm font-mono">{e.odometer_reading || 0}</TableCell>
                    <TableCell className="text-sm font-bold text-emerald-700">{e.mileage_kmpl > 0 ? `${e.mileage_kmpl} km/l` : "\u2014"}</TableCell>
                    <TableCell className="text-xs">{e.created_at ? formatDateIST(e.created_at) : "\u2014"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Entry Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle style={{ fontFamily: "Chivo" }}>
                {currentDraftId ? <span className="flex items-center gap-2"><FileEdit size={16} className="text-amber-600" /> Continue Draft</span> : formTitles[tab]}
              </DialogTitle>
              {lastAutoSave && <span className="text-[10px] text-gray-400">Draft saved {formatTimeIST(lastAutoSave)}</span>}
            </div>
          </DialogHeader>
          {tab === "gate" && <VehicleEntryForm form={form} setField={setField} vehicleTypes={vehicleTypes} parties={parties} onSelectOrOther={handleSelectOrOther} setOtherField={setOtherField} />}
          {tab === "meter" && <MeterLogForm form={form} setField={setField} vehicles={vehicles} />}
          {tab === "fuel" && <FuelLogForm form={form} setField={setField} vehicles={vehicles} />}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={closeForm} className="h-11">Cancel</Button>
            {tab === "gate" && (
              <Button variant="outline" onClick={handleSaveDraft} className="h-11 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="save-draft-btn">
                <FileEdit size={14} className="mr-1" /> Save Draft
              </Button>
            )}
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1B3C87] hover:bg-[#2650A8] h-11" data-testid="save-entry-btn">
              {saving ? "Saving..." : "Submit Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OtherValueDialog
        otherField={otherField}
        otherValue={otherValue}
        setOtherValue={setOtherValue}
        onSubmit={handleOtherSubmit}
        onClose={() => { setOtherField(null); setOtherValue(""); }}
      />

      <EntryDetailDialog open={!!detailEntry} onClose={() => setDetailEntry(null)} entry={detailEntry} title={tab === "gate" ? "Vehicle Details" : tab === "meter" ? "Meter Log Details" : "Fuel Log Details"} collection={cfg.collection} onRefresh={fetchEntries} />

      {/* Close Trip Dialog — record return odometer reading on open meter log entry */}
      <Dialog open={!!closeTripEntry} onOpenChange={(o) => { if (!o) { setCloseTripEntry(null); setCloseTripForm({}); } }}>
        <DialogContent className="max-w-md" data-testid="close-trip-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>Close Trip — Record Return Reading</DialogTitle>
          </DialogHeader>
          {closeTripEntry && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded p-3 text-xs space-y-1 border">
                <p><span className="text-gray-500">Vehicle:</span> <strong>{closeTripEntry.vehicle_no}</strong></p>
                <p><span className="text-gray-500">Driver:</span> {closeTripEntry.driver_name || closeTripEntry.employee_name || "—"}</p>
                <p><span className="text-gray-500">Out Reading (start km):</span> <strong className="font-mono">{closeTripEntry.odometer_out} km</strong></p>
                <p><span className="text-gray-500">Ref:</span> <span className="font-mono">{closeTripEntry.ref_no}</span></p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">In Reading (Return Km) *</Label>
                <Input type="number" step="0.1" value={closeTripForm.odometer_in || ""} onChange={(e) => setCloseTripForm((p) => ({ ...p, odometer_in: e.target.value }))} className="h-11" placeholder={`Must be ≥ ${closeTripEntry.odometer_out}`} data-testid="close-trip-odo-in" autoFocus />
              </div>
              {closeTripForm.odometer_in > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Km Run (auto)</p>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="close-trip-km-run">
                    {Math.max(0, (parseFloat(closeTripForm.odometer_in) || 0) - (parseFloat(closeTripEntry.odometer_out) || 0)).toFixed(1)} km
                  </p>
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Return Remarks (optional)</Label>
                <Input value={closeTripForm.remarks || ""} onChange={(e) => setCloseTripForm((p) => ({ ...p, remarks: e.target.value }))} className="h-11" data-testid="close-trip-remarks" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCloseTripEntry(null); setCloseTripForm({}); }} className="h-11">Cancel</Button>
            <Button
              onClick={async () => {
                if (!closeTripEntry?.id) return;
                const odoIn = parseFloat(closeTripForm.odometer_in);
                if (!odoIn || odoIn <= 0) { toast.error("Return odometer reading is required"); return; }
                if (odoIn < parseFloat(closeTripEntry.odometer_out)) { toast.error(`Return reading must be ≥ ${closeTripEntry.odometer_out}`); return; }
                try {
                  const { data } = await api.put(`/mod5/meter-log/${closeTripEntry.id}/close`, closeTripForm);
                  toast.success(data.message || "Trip closed");
                  setCloseTripEntry(null); setCloseTripForm({}); fetchEntries();
                } catch (err) { toast.error(err.response?.data?.detail || "Failed to close trip"); }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 h-11"
              data-testid="close-trip-submit"
            >
              Close Trip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
